
import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Check } from "lucide-react";
import { devLog, devError } from "@/utils/devLog";

interface CoursePreviewProps {
  courseId: string;
  courseName: string;
  price: string;
}

export const CoursePreview: React.FC<CoursePreviewProps> = ({
  courseId,
  courseName,
  price
}) => {
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState<{
    isEnrolled: boolean;
    status: string | null;
  } | null>(null);
  
  // Check if the user is already enrolled in this course
  useEffect(() => {
    const checkEnrollment = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user || !courseId) return;
        
        devLog("[CoursePreview] Checking enrollment status", { 
          userId: user.id, 
          courseId 
        });
        
        const { data, error } = await supabase
          .from('enrollments')
          .select('id, status')
          .eq('course_id', courseId)
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (error) {
          devError("[CoursePreview] Error checking enrollment:", error);
          return;
        }
        
        devLog("[CoursePreview] Enrollment check result:", data);
        
        if (data) {
          setEnrollmentStatus({
            isEnrolled: true,
            status: data.status
          });
        } else {
          setEnrollmentStatus({
            isEnrolled: false,
            status: null
          });
        }
      } catch (err) {
        devError("[CoursePreview] Error checking enrollment:", err);
      }
    };
    
    checkEnrollment();
  }, [courseId]);

  // Detailed logging for course details fetch
  useEffect(() => {
    const fetchCourseDetails = async () => {
      if (!courseId) {
        devLog("[CoursePreview] No course ID provided");
        setError("No course ID provided");
        setLoading(false);
        return;
      }

      try {
        devLog(`[CoursePreview] Attempting to fetch course details for ID: ${courseId}`);
        
        const { data, error } = await supabase
          .from("courses")
          .select("thumbnail_url, description, published")  // Add published to select
          .eq("id", courseId)
          .single();

        if (error) {
          devError("[CoursePreview] Course fetch error:", error);
          setError(`Error loading course: ${error.message}`);
          setLoading(false);
          return;
        }

        // Log additional details including published status
        devLog("[CoursePreview] Course fetch successful:", {
          thumbnailUrl: data.thumbnail_url,
          description: data.description,
          published: data.published
        });
        
        setThumbnail(data.thumbnail_url);
        setDescription(data.description);

        // If course is not published and user is not a platform owner, set error
        if (!data.published) {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            devLog("[CoursePreview] Unauthorized access to unpublished course");
            setError("This course is not published");
          }
        }
      } catch (err: any) {
        devError("[CoursePreview] Unexpected course-fetch failure:", err);
        setError(err.message || "Unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchCourseDetails();
  }, [courseId]);

  const formattedPrice = parseFloat(price) > 0
    ? `$${parseFloat(price).toFixed(2)}`
    : "Free";

  return (
    <Card className="overflow-hidden">
      {loading ? (
        <div className="space-y-4 p-6">
          <Skeleton className="h-52 w-full" />
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ) : error ? (
        <div className="p-6 flex flex-col items-center text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" aria-hidden="true" />
          <h3 className="text-xl font-bold mb-2">Course Loading Error</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      ) : (
        <>
          {thumbnail ? (
            <div className="h-52 bg-cover bg-center" 
                 style={{ backgroundImage: `url(${thumbnail})` }}
                 role="img"
                 aria-label={`${courseName} course thumbnail`}>
            </div>
          ) : (
            <div className="h-52 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
              <span className="text-primary-800 text-2xl font-bold">
                {courseName}
              </span>
            </div>
          )}
          <div className="p-6">
            <h3 className="text-xl font-bold mb-2">{courseName}</h3>
            <div className="mb-4">
              <span className="text-lg font-semibold text-primary">
                {formattedPrice}
              </span>
            </div>
            {description && (
              <p className="text-gray-600 text-sm line-clamp-4 mb-4">{description}</p>
            )}
            
            {enrollmentStatus && (
              <div className={`p-2 rounded-md text-sm mb-4 ${
                enrollmentStatus.isEnrolled 
                  ? "bg-green-50 text-green-700 border border-green-200" 
                  : "bg-blue-50 text-blue-700 border border-blue-200"
              }`}>
                {enrollmentStatus.isEnrolled ? (
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4" aria-hidden="true" />
                    <span>
                      Already enrolled 
                      {enrollmentStatus.status && enrollmentStatus.status !== 'active' 
                        ? ` (${enrollmentStatus.status})` 
                        : ''}
                    </span>
                  </div>
                ) : (
                  <span>Ready for enrollment</span>
                )}
              </div>
            )}
            
            <div className="text-sm text-gray-500">
              <p>Sign in to continue with enrollment</p>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};
